import { useMemo, useState } from 'react';
import { Button, Checkbox, Modal, Select } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import { useTransferOwnership } from '@/hooks/useSpaceManagement';
import { displayName } from '@/lib/user-display';
import type { SpaceMembership } from '@/types/spaces';

interface TransferOwnershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  spaceId: string;
  members: SpaceMembership[];
  currentOwnerUserId: string | undefined;
}

export function TransferOwnershipModal({
  isOpen,
  onClose,
  spaceId,
  members,
  currentOwnerUserId,
}: TransferOwnershipModalProps) {
  const { success, error: showError } = useToast();
  const transfer = useTransferOwnership(spaceId);
  const [targetUserId, setTargetUserId] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const candidates = useMemo(
    () => members.filter((m) => m.userId !== currentOwnerUserId),
    [members, currentOwnerUserId],
  );

  const options = useMemo(
    () => [
      { value: '', label: 'Pick a member…' },
      ...candidates.map((m) => ({ value: m.userId, label: `${displayName(m)} (${m.role})` })),
    ],
    [candidates],
  );

  const reset = () => {
    setTargetUserId('');
    setConfirmed(false);
  };

  const handleClose = () => {
    if (transfer.isPending) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!targetUserId || !confirmed) return;
    try {
      await transfer.mutateAsync(targetUserId);
      success('Ownership transferred');
      reset();
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to transfer ownership');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Transfer Ownership"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={transfer.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmit}
            isLoading={transfer.isPending}
            disabled={!targetUserId || !confirmed}
          >
            Transfer Ownership
          </Button>
        </>
      }
    >
      <div className="space-y-4 py-2">
        <p className="text-sm text-muted-foreground">
          The new owner gains full control of this space. You'll be demoted to admin and won't be able to undo this without their cooperation.
        </p>

        <Select
          label="New owner"
          options={options}
          value={targetUserId}
          onChange={(e: { target: { value: string } }) => setTargetUserId(e.target.value)}
        />

        <label className="flex items-start gap-2 text-sm text-foreground">
          <Checkbox
            checked={confirmed}
            onCheckedChange={(checked: boolean | 'indeterminate') => setConfirmed(checked === true)}
          />
          <span>I understand this is irreversible without the new owner's consent.</span>
        </label>
      </div>
    </Modal>
  );
}
